import { useState, useEffect, memo, useCallback } from "react";
import { CSVLink } from "react-csv";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import BotonSOS from "./sos/boton_sos";
import { consultarSimple, actualizarRegistro, eliminarRegistro } from "../../functions/crud";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";

const RUTA = "notificaciones_excluidas";

const MOTIVO_DEFECTO = "Solicito darse de baja de la base de datos mediante el correo de servicio al cliente.";

const columnas = [
  { label: "Correo", key: "correo" },
  { label: "Motivo", key: "motivo" },
];

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const limpiarCorreo = (correo) => correo.replaceAll("@", "").replaceAll(".", "");

const CorreosExcluidos = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const capturarDatos = async () => {
    try {
      const data = await consultarSimple(RUTA);
      const lista = Object.entries(data || {}).map(([clave, motivo]) => ({
        clave,
        correo: clave,
        motivo: typeof motivo === "string" ? motivo : "",
      }));
      setDatos(lista);
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    capturarDatos();
  }, []);

  const formularioHtml = (registro = {}) => `
    <style>
      #ce-form .swal2-input, #ce-form textarea.swal2-input {
        margin: 0 !important; width: 100% !important; box-sizing: border-box !important;
        padding: 10px !important; border: 1px solid #d1d5db !important;
        border-radius: 5px !important; font-size: 14px !important;
      }
      #ce-form textarea.swal2-input {
        min-height: 120px !important; resize: vertical !important;
        font-family: inherit !important; line-height: 1.5 !important;
      }
      #ce-form label { display:block; margin-bottom:6px; color:#555; font-size:14px; font-weight:500; }
      #ce-form .ce-field { margin-bottom:15px; }
    </style>
    <div id="ce-form" style="text-align:left;padding:10px 20px 0;">
      <div class="ce-field">
        <label>Correo electrónico</label>
        <input id="ce-correo" type="email" class="swal2-input" placeholder="ejemplo@correo.com" value="${registro.correoOriginal || ""}" ${registro.clave ? "readonly style='background:#f9fafb;color:#6b7280;'" : ""}>
      </div>
      <div class="ce-field">
        <label>Motivo de la exclusión</label>
        <textarea id="ce-motivo" class="swal2-input" rows="3" placeholder="Ej: Solicito darse de baja de la base de datos...">${registro.motivo || ""}</textarea>
      </div>
    </div>
  `;

  const leerFormulario = (esNuevo) => () => {
    const correo = document.getElementById("ce-correo").value.trim().toLowerCase();
    const motivo = document.getElementById("ce-motivo").value.trim();

    if (esNuevo) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(correo)) {
        Swal.showValidationMessage("Ingresa un correo electrónico válido");
        return false;
      }
    }
    if (!motivo) {
      Swal.showValidationMessage("El motivo es obligatorio");
      return false;
    }
    return { correo, motivo };
  };

  const agregarCorreo = useCallback(async () => {
    const { value: formValues } = await Swal.fire({
      title: "Agregar Correo Excluido",
      html: formularioHtml({ motivo: MOTIVO_DEFECTO }),
      showCancelButton: true,
      confirmButtonText: "Agregar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
      width: 550,
      preConfirm: leerFormulario(true),
    });

    if (!formValues) return;

    try {
      const clave = limpiarCorreo(formValues.correo);
      await actualizarRegistro(`${RUTA}/${clave}`, formValues.motivo);
      await Swal.fire({ icon: "success", title: "Correo excluido", text: "El correo fue agregado a la lista.", confirmButtonColor: "#c0392b" });
      capturarDatos();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo agregar el correo.", confirmButtonColor: "#c0392b" });
    }
  }, []);

  const editarCorreo = useCallback(async (fila) => {
    const { value: formValues } = await Swal.fire({
      title: "Editar Motivo",
      html: formularioHtml({ clave: fila.clave, correoOriginal: fila.correo, motivo: fila.motivo }),
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
      width: 550,
      preConfirm: leerFormulario(false),
    });

    if (!formValues) return;

    try {
      await actualizarRegistro(`${RUTA}/${fila.clave}`, formValues.motivo);
      await Swal.fire({ icon: "success", title: "Actualizado", text: "El motivo fue actualizado.", confirmButtonColor: "#c0392b" });
      capturarDatos();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar.", confirmButtonColor: "#c0392b" });
    }
  }, []);

  const eliminarCorreo = useCallback(async (fila) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Quitar de la lista?",
      html: `
        <div style="text-align:left;padding:15px;">
          <div style="background:#f8f9fa;padding:15px;border-radius:8px;border-left:4px solid #c0392b;">
            <strong style="display:block;margin-bottom:8px;color:#2c3e50;">${fila.correo}</strong>
            <p style="color:#7f8c8d;margin:0;font-size:14px;">${fila.motivo || "-"}</p>
          </div>
          <p style="margin-top:15px;color:#c0392b;">El correo volverá a recibir notificaciones.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c0392b",
      confirmButtonText: "Quitar",
      cancelButtonText: "Cancelar",
    });

    if (!isConfirmed) return;

    try {
      await eliminarRegistro(`${RUTA}/${fila.clave}`);
      Swal.fire({ title: "Quitado", text: "El correo fue removido de la lista.", icon: "success", timer: 2000, showConfirmButton: false });
      capturarDatos();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo quitar el correo.", confirmButtonColor: "#c0392b" });
    }
  }, []);

  const botonesAccion = [
    { texto: "Editar", icono: "bi-pencil", accion: editarCorreo },
    { texto: "Quitar", icono: "bi-trash", accion: eliminarCorreo },
  ];

  return cargando ? (
    <AnimacionCarga />
  ) : (
    <>
      <TablaGenerica
        datos={datos}
        titulos={columnas.map((c) => c.label.toUpperCase())}
        campos={columnas.map((c) => c.key)}
        elementosPorPagina={500}
        botones={botonesAccion}
        contenidoExtra={
          <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            <CSVLink data={datos} headers={columnas} filename="correos_excluidos.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
            <button onClick={agregarCorreo} title="Agregar Correo"><i className="bi bi-plus-lg" /></button>
          </div>
        }
      />
      <BotonSOS />
    </>
  );
});

export default CorreosExcluidos;
